/**
 * FERP Document Viewer Page (DRR-079)
 *
 * Standalone PDF viewer connecting existing PDFViewer, AnnotationLayer,
 * PDFToolbar, and SignatureCapture components to backend mutations.
 *
 * Route: /ferp-core/documents/:documentId
 *
 * FERP Principles:
 * - Dumb Frontend: document + annotations from backend via GraphQL
 * - Mantine v8.x ONLY
 * - Backend IDs only — no frontend ID generation
 * - All mutations via codegen hooks
 */

import { useState, useCallback, useEffect } from 'react';
import { useTenantId } from '@/auth/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Alert,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconFileDescription,
  IconAlertCircle,
  IconArrowLeft,
  IconSignature,
  IconDownload,
  IconFileCode,
} from '@tabler/icons-react';
// Apollo hooks imported from generated/graphql

import { PDFViewer, SignatureCapture, usePdfExport } from '@/components/pdf';
import type { PdfAnnotation, PdfFeatureTier, WatermarkType, DocumentTrailMeta } from '@/components/pdf';
import {
  DocumentStatus,
  type Document as GqlDocument,
  useGetDocumentQuery,
  useRequestDocumentDownloadUrlMutation,
  useSavePdfAnnotationsMutation,
  useSignDocumentMutation,
  useRenderHtmlPdfMutation,
} from '@/generated/graphql';

interface GetDocumentData {
  getDocument: GqlDocument | null;
}

interface RequestDownloadUrlData {
  requestDocumentDownloadUrl: {
    downloadUrl: string;
    documentName: string;
    mimeType: string;
    expiresInSeconds: number;
  };
}

function statusColor(status: string): string {
  switch (status) {
    case DocumentStatus.APPROVED:
    case DocumentStatus.PUBLISHED:
      return 'green';
    case DocumentStatus.PENDING_APPROVAL:
    case DocumentStatus.PENDING_VALIDATION:
      return 'blue';
    case DocumentStatus.REJECTED:
      return 'red';
    case DocumentStatus.ARCHIVED:
      return 'orange';
    case DocumentStatus.DRAFT:
      return 'gray';
    default:
      return 'gray';
  }
}

/** Parse annotation JSON from Document.dynamodbContent */
function parseAnnotations(dynamodbContent: string | null | undefined): PdfAnnotation[] {
  if (!dynamodbContent) return [];
  try {
    const parsed = JSON.parse(dynamodbContent);
    if (Array.isArray(parsed)) return parsed as PdfAnnotation[];
    if (parsed.annotations && Array.isArray(parsed.annotations)) {
      return parsed.annotations as PdfAnnotation[];
    }
    return [];
  } catch {
    return [];
  }
}

export function DocumentViewerPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const tenantId = useTenantId();
  const navigate = useNavigate();
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  const { data, loading, error } = useGetDocumentQuery({
    variables: { tenantId: tenantId ?? '', documentId: documentId ?? '' },
    skip: !documentId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const doc = data?.getDocument ?? null;

  // Load annotations from dynamodbContent on first data arrival
  useEffect(() => {
    if (!annotationsLoaded && doc) {
      setAnnotations(parseAnnotations(doc.dynamodbContent));
      setAnnotationsLoaded(true);
    }
  }, [doc, annotationsLoaded]);

  // Fetch presigned download URL when document loads
  const [requestDownloadUrl] = useRequestDocumentDownloadUrlMutation();

  useEffect(() => {
    if (!doc || !documentId || doc.mimeType !== 'application/pdf') return;
    if (!doc.s3Key || !doc.s3Bucket) return;

    requestDownloadUrl({
      variables: { input: { documentId, tenantId: tenantId ?? '' } },
    }).then((result) => {
      const url = result.data?.requestDocumentDownloadUrl?.downloadUrl;
      if (url) setDocumentUrl(url);
    }).catch((err) => {
      console.error('Failed to get document download URL:', err);
    });
  }, [doc, documentId, requestDownloadUrl]);

  // Generate sample PDF from template
  const [renderHtmlPdf, { loading: generatingSample }] = useRenderHtmlPdfMutation();

  // Detect if this is a template document (no actual file in S3)
  const isTemplate = doc?.category === 'BatchPdf' ||
                     doc?.category === 'TEMPLATE' ||
                     (!doc?.s3Key && doc?.documentName?.toLowerCase().includes('template'));

  const handleGenerateSample = useCallback(async () => {
    if (!doc || !tenantId) return;

    // Use documentId as template_id (templates are stored with their ID as document ID)
    const templateId = doc.documentId;

    // Sample variables for demo - in production these would come from a form
    const sampleVariables = {
      facility_name: 'Hillside Harmony Assisted Living',
      facility_license: 'ALF-12345',
      facility_address: '123 Care Lane, Minneapolis, MN 55401',
      facility_phone: '(612) 555-0100',
      staff_name: 'Sample Staff Member',
      staff_role: 'Unlicensed Personnel',
      staff_hire_date: new Date().toISOString().split('T')[0],
      staff_id: 'STAFF-001',
      competency_area: 'Sample Competency',
      competency_name: 'Sample Training Module',
      statutory_ref: 'MN 144G.61 Subd 2',
      source_document: 'Care Providers of MN Training Manual',
      deadline_working_hours: '80',
      completion_date: new Date().toISOString().split('T')[0],
      completion_status: 'VERIFIED',
      evaluator_name: 'Jane Smith, RN',
      evaluator_title: 'Director of Nursing',
      assessment_criteria: [
        { checkmark: '✓', description: 'Demonstrates proper technique', safety_badge: '' },
        { checkmark: '✓', description: 'Follows safety protocols', safety_badge: '⚠️ Safety Critical' },
      ],
      generated_date: new Date().toISOString(),
      document_id: `SAMPLE-${Date.now()}`,
    };

    try {
      const result = await renderHtmlPdf({
        variables: {
          input: {
            tenantId,
            templateId,
            variablesJson: JSON.stringify(sampleVariables),
          },
        },
      });

      if (result.data?.renderHtmlPdf?.downloadUrl) {
        setDocumentUrl(result.data.renderHtmlPdf.downloadUrl);
      }
    } catch (err) {
      console.error('Failed to generate sample PDF:', err);
    }
  }, [doc, tenantId, renderHtmlPdf]);

  const isPdf = doc?.mimeType === 'application/pdf';

  // Watermark for DRAFT documents
  const watermark: WatermarkType =
    doc?.currentStatus === DocumentStatus.DRAFT ? 'DRAFT' : null;

  // Feature tier — in production this comes from tenant subscription via backend.
  // For dev/demo, default to 'pro'. Change to 'free' to test the free tier.
  const featureTier: PdfFeatureTier = 'pro';

  // Document trail metadata for the right-panel Trail tab
  const trailMeta: DocumentTrailMeta | null = doc ? {
    workflowInstanceId: null,
    taskId: null,
    approvalWorkflowId: null,
    approverActorIds: [],
    requiredApproverRoleIds: [],
    regulatoryRuleIds: doc.regulatoryRuleIds ?? [],
    regulatoryEvidenceName: doc.regulatoryEvidenceName ?? null,
    regulatoryDeadline: null,
    complianceFrameworks: [],
    policyId: null,
    validationId: null,
    version: String(doc.version),
    previousVersionId: null,
    versionHistoryIds: [],
    versionChangeDescription: null,
    versionCreatedAt: String(doc.createdAt),
    versionCreatedBy: '',
    createdAt: String(doc.createdAt),
    createdBy: '',
    updatedAt: String(doc.updatedAt),
    updatedBy: '',
    statusUpdatedAt: String(doc.updatedAt),
    statusUpdatedBy: '',
    currentStatus: String(doc.currentStatus),
    sourceChaosDocumentId: null,
  } : null;

  // Mutations
  const [savePdfAnnotations, { loading: savingAnnotations }] = useSavePdfAnnotationsMutation();
  const [signDocument, { loading: signingDocument }] = useSignDocumentMutation();

  // PDF export hook
  const { exporting, exportPdf } = usePdfExport(
    documentUrl ?? '',
    annotations,
    doc?.documentName ?? 'document',
  );

  const handleAnnotationsChange = useCallback((newAnnotations: PdfAnnotation[]) => {
    setAnnotations(newAnnotations);
  }, []);

  const handleSaveAnnotations = useCallback(async () => {
    if (!documentId) return;
    await savePdfAnnotations({
      variables: {
        input: {
          documentId,
          tenantId: '',
          annotationsJson: JSON.stringify(annotations),
        },
      },
    });
  }, [documentId, annotations, savePdfAnnotations]);

  const handleSign = useCallback(
    async (signatureDataUrl: string, signerId: string) => {
      if (!documentId) return;
      await signDocument({
        variables: {
          input: {
            documentId,
            tenantId: '',
            signatureImage: signatureDataUrl,
            signerId,
          },
        },
      });
      setSignModalOpen(false);
    },
    [documentId, signDocument],
  );

  if (!documentId) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" icon={<IconAlertCircle />}>
          No document ID provided
        </Alert>
      </Container>
    );
  }

  if (loading && !doc) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" icon={<IconAlertCircle />}>
          Failed to load document: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!doc) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" icon={<IconAlertCircle />}>
          Document not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/ferp-core/documents')}
            >
              Back
            </Button>
            <IconFileDescription size={24} />
            <Title order={3}>{doc.documentName}</Title>
          </Group>
          <Group>
            <Badge size="lg" color={statusColor(String(doc.currentStatus))}>
              {doc.currentStatus}
            </Badge>
            <Text size="sm" c="dimmed">v{doc.version}</Text>
          </Group>
        </Group>

        {/* Document metadata */}
        <Group gap="xl">
          <Text size="sm">
            <Text span fw={600}>Type:</Text> {doc.documentType}
          </Text>
          <Text size="sm">
            <Text span fw={600}>Category:</Text> {doc.category}
          </Text>
          <Text size="sm">
            <Text span fw={600}>Size:</Text> {(Number(doc.fileSizeBytes) / 1024).toFixed(1)} KB
          </Text>
          <Text size="sm">
            <Text span fw={600}>Created:</Text> {new Date(String(doc.createdAt)).toLocaleDateString()}
          </Text>
        </Group>

        {/* PDF Viewer or fallback */}
        {isPdf && documentUrl ? (
          <>
            <PDFViewer
              url={documentUrl}
              documentId={String(doc.documentId)}
              annotations={annotations}
              onAnnotationsChange={handleAnnotationsChange}
              onRequestSign={() => setSignModalOpen(true)}
              onRequestExport={exportPdf}
              canAnnotate
              canSign
              variant="full"
              watermark={watermark}
              featureTier={featureTier}
              documentTrailMeta={trailMeta}
            />

            {/* Action bar */}
            <Group justify="flex-end">
              <Button
                variant="light"
                loading={savingAnnotations}
                onClick={handleSaveAnnotations}
                disabled={annotations.length === 0}
              >
                Save Annotations ({annotations.length})
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                loading={exporting}
                onClick={exportPdf}
              >
                Export with Annotations
              </Button>
              <Button
                leftSection={<IconSignature size={16} />}
                onClick={() => setSignModalOpen(true)}
                loading={signingDocument}
              >
                Sign Document
              </Button>
            </Group>

            <SignatureCapture
              opened={signModalOpen}
              onClose={() => setSignModalOpen(false)}
              onSign={handleSign}
              documentName={doc.documentName}
              signing={signingDocument}
            />
          </>
        ) : isTemplate ? (
          <Stack gap="md">
            <Alert color="blue" variant="light" icon={<IconFileCode size={16} />}>
              This is a PDF template. Click "Generate Sample" to render a sample PDF with placeholder data.
            </Alert>
            <Button
              leftSection={<IconFileCode size={16} />}
              onClick={handleGenerateSample}
              loading={generatingSample}
            >
              Generate Sample PDF
            </Button>
          </Stack>
        ) : (
          <Alert color="blue" variant="light" icon={<IconFileDescription size={16} />}>
            {!isPdf
              ? `This document (${doc.mimeType}) is not a PDF. PDF viewer is only available for PDF documents.`
              : 'Document URL is not available. The document may still be processing or the storage backend is not configured.'}
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
